import React, {useCallback, useEffect, useRef, useState} from 'react'
import {observer} from 'mobx-react-lite'
import {flowResult, runInAction} from 'mobx'
import {usePagination} from 'react-instantsearch-hooks-web'
import {Box, Button, Flex} from '../../../vanilla'
import {useProductStore, useGlobalStore} from '../../../store/hooks/useStore'
import {Hit} from '../../../store/ProductStore'
import {getProductListDefaults} from '../../../constants'
import {PLPSkeletonTiles} from '../../skeletons'
import {hitsGrid} from './styles.css'
import {isMobile} from '../../../utils/utils'

interface LoadMoreProps {
  isInitialResults: boolean
  onInitialResultsLoaded: () => void
}

export const LoadPrevious = observer(({isInitialResults}: {isInitialResults: boolean}) => {
  const {refine} = usePagination()
  const {firstLoadedPage, prependAccumulatedHits, setFirstLoadedPage, fetchAdditionalHitsPage} = useProductStore()
  const [isFetchingPreviousPage, setIsFetchingPreviousPage] = useState(false)
  const {customSitePreferences} = useGlobalStore()
  const {initialResultsBatches, resultsSize} = getProductListDefaults(customSitePreferences)
  const isMobileFlag = isMobile()

  if (isInitialResults || firstLoadedPage <= 0) {
    return null
  }

  const loadPreviousPage = () => {
    if (isFetchingPreviousPage) return

    const firstPageToFetch = Math.max(0, firstLoadedPage - initialResultsBatches)
    setIsFetchingPreviousPage(true)

    const fetchPreviousBatch = async () => {
      const previousHits = new Map<string, Hit>()

      for (let page = firstPageToFetch; page < firstLoadedPage; page += 1) {
        const {hits} = (await flowResult(fetchAdditionalHitsPage(page))) as {hits: Hit[]}
        hits.forEach((hit) => {
          if (!previousHits.has(hit.objectID)) {
            previousHits.set(hit.objectID, hit)
          }
        })
      }

      prependAccumulatedHits([...previousHits.values()])
      setFirstLoadedPage(firstPageToFetch)
      refine(firstPageToFetch)
      setIsFetchingPreviousPage(false)
    }

    fetchPreviousBatch().catch(() => setIsFetchingPreviousPage(false))
  }

  return (
    <>
      <Flex justify="center">
        <Button
          variant={!isFetchingPreviousPage ? 'outlinePrimary' : 'secondary'}
          onClick={loadPreviousPage}
          isLoading={isFetchingPreviousPage}
          data-test-selector="load-previous-button"
          width={isMobileFlag ? 'full' : 'auto'}
        >
          Load Previous
        </Button>
      </Flex>
      {isFetchingPreviousPage && (
        <Box className={hitsGrid}>
          <PLPSkeletonTiles
            count={Math.min(firstLoadedPage, initialResultsBatches) * resultsSize}
          />
        </Box>
      )}
    </>
  )
})

export const LoadMore: React.FC<LoadMoreProps> = observer(
  ({isInitialResults, onInitialResultsLoaded}) => {
    const {currentRefinement, refine} = usePagination()
    const {
      backfillTargetPage,
      setBackfillTargetPage,
      loadedPageCount,
      setLoadedPageCount,
      setFirstLoadedPage,
      totalPages,
      setTotalPages,
      searchSignature,
      appendAccumulatedHits,
      resetAccumulatedHits,
      fetchAdditionalHitsPage
    } = useProductStore()
    const {customSitePreferences} = useGlobalStore()
    const {resultsSize, initialResultsBatches, autoLoadThreshold} =
      getProductListDefaults(customSitePreferences)
    const resultsEndRef = useRef<HTMLDivElement>(null)
    const lastSearchSignatureRef = useRef<string | null>(null)
    const backfillStartPageRef = useRef<number | null>(null)
    const isFetchingRef = useRef(false)
    const searchGenerationRef = useRef(0)
    const [isFetchingNextPage, setIsFetchingNextPage] = useState(false)
    const [refetchTrigger, setRefetchTrigger] = useState(0)
    const isMobileFlag = isMobile()

    const loadedPageCountRef = useRef(loadedPageCount)
    loadedPageCountRef.current = loadedPageCount
    const totalPagesRef = useRef(totalPages)
    totalPagesRef.current = totalPages

    const syncUrlToPage = useCallback((page: number) => {
      if (page >= 0 && page !== currentRefinement) {
        refine(page)
      }
    }, [currentRefinement, refine])

    const [autoNextEnabled, setAutoNextEnabled] = useState(false)
    const autoNextCounterRef = useRef(1)

    useEffect(() => {
      if (!searchSignature || lastSearchSignatureRef.current === searchSignature) return

      const isFirstSearch = lastSearchSignatureRef.current === null
      lastSearchSignatureRef.current = searchSignature
      searchGenerationRef.current += 1

      const isDeepLinked = isFirstSearch && currentRefinement > initialResultsBatches - 1

      resetAccumulatedHits([])
      setTotalPages(null)
      setLoadedPageCount(0)
      // Deep-linking mid-list also backfills 2 batches prior
      const deepLinkedPage = isDeepLinked ? Math.max(0, currentRefinement - 2) : null
      backfillStartPageRef.current = deepLinkedPage
      setFirstLoadedPage(deepLinkedPage ?? 0)
      setBackfillTargetPage(isDeepLinked ? currentRefinement : initialResultsBatches - 1)
    }, [searchSignature])

    useEffect(() => {
      if (backfillTargetPage === null || isFetchingRef.current) return

      const reachedTarget = loadedPageCount > backfillTargetPage
      const reachedEnd = totalPages !== null && loadedPageCount >= totalPages

      if (reachedTarget || reachedEnd) {
        setBackfillTargetPage(null)
        onInitialResultsLoaded()
        syncUrlToPage(loadedPageCount - 1)
        return
      }

      isFetchingRef.current = true
      const generation = searchGenerationRef.current
      const pageToFetch =
        loadedPageCount === 0 && backfillStartPageRef.current !== null
          ? backfillStartPageRef.current
          : loadedPageCount

      flowResult(fetchAdditionalHitsPage(pageToFetch))
        .then(({hits, nbPages}: {hits: Hit[]; nbPages: number}) => {
          isFetchingRef.current = false
          setRefetchTrigger((tick) => tick + 1)
          if (searchGenerationRef.current !== generation) return
          appendAccumulatedHits(hits)
          setTotalPages(nbPages)
          setLoadedPageCount(pageToFetch + 1)
        })
        .catch(() => {
          isFetchingRef.current = false
          setRefetchTrigger((tick) => tick + 1)
        })
    }, [backfillTargetPage, loadedPageCount, totalPages, refetchTrigger])

    const loadNextPage = useCallback((source: 'manual' | 'auto' = 'manual') => {
      if (isFetchingRef.current) return
      if (totalPagesRef.current !== null && loadedPageCountRef.current >= totalPagesRef.current)
        return

      isFetchingRef.current = true
      setIsFetchingNextPage(true)

      if (source === 'auto') {
        autoNextCounterRef.current += 1
        if (autoNextCounterRef.current >= autoLoadThreshold) {
          setAutoNextEnabled(false)
          autoNextCounterRef.current = 1
        }
      } else {
        setAutoNextEnabled(true)
      }

      const generation = searchGenerationRef.current
      const pageToFetch = loadedPageCountRef.current

      const revertTimeout = setTimeout(() => setIsFetchingNextPage(false), 5000)

      flowResult(fetchAdditionalHitsPage(pageToFetch))
        .then(({hits, nbPages}: {hits: Hit[]; nbPages: number}) => {
          clearTimeout(revertTimeout)
          isFetchingRef.current = false
          setIsFetchingNextPage(false)
          setRefetchTrigger((tick) => tick + 1)
          if (searchGenerationRef.current !== generation) return
          appendAccumulatedHits(hits)
          setTotalPages(nbPages)
          setLoadedPageCount(pageToFetch + 1)
          syncUrlToPage(pageToFetch)
        })
        .catch(() => {
          clearTimeout(revertTimeout)
          isFetchingRef.current = false
          setIsFetchingNextPage(false)
          setRefetchTrigger((tick) => tick + 1)
        })
    }, [autoLoadThreshold, fetchAdditionalHitsPage, appendAccumulatedHits, setTotalPages, setLoadedPageCount, syncUrlToPage])

    useEffect(() => {
      if (!autoNextEnabled || isInitialResults) return
      const node = resultsEndRef.current
      if (!node) return

      let hasTriggeredForCurrentView = false

      const intersectionObserver = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            if (!hasTriggeredForCurrentView) {
              hasTriggeredForCurrentView = true
              loadNextPage('auto')
            }
          } else {
            hasTriggeredForCurrentView = false
          }
        },
        {rootMargin: '0px 0px 70px 0px'}
      )

      intersectionObserver.observe(node)
      return () => intersectionObserver.disconnect()
    }, [autoNextEnabled, isInitialResults, loadNextPage])

    const isLastLoadedPage = totalPages !== null && loadedPageCount >= totalPages

    if (!isInitialResults && isLastLoadedPage) {
      return null
    }

    return (
      <Box ref={resultsEndRef}>
        {!isInitialResults && (
          <>
            {isFetchingNextPage && (
              <Box className={hitsGrid} style={{marginBottom: '24px'}}>
                <PLPSkeletonTiles count={resultsSize} />
              </Box>
            )}
            <Flex justify="center">
              <Button
                variant={!isFetchingNextPage ? 'outlinePrimary' : 'secondary'}
                onClick={() => loadNextPage('manual')}
                isLoading={isFetchingNextPage}
                data-test-selector="load-more-button"
                width={isMobileFlag ? 'full' : 'auto'}
              >
                Load more
              </Button>
            </Flex>
          </>
        )}
      </Box>
    )
  }
)
